import { useEffect, useMemo, useState } from 'react'

import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import {
  fetchCampaignMetrics,
  fetchCampaigns,
  publishCampaign,
} from '@/services/campaigns'
import { api } from '@/services/api'
import { useDispatch } from 'react-redux'
import { handleSetCampaigns } from '@/storage/modules/campaigns/reducer'
import { CampaignDataProps } from '@/types/campaignProps'
import { UserDataProps } from '@/types/userProps'

import { Button } from '../Button'
import { Select } from '../form/Select'

interface FormPublishCampaignProps {
  campaign: CampaignDataProps
}

type AudienceFilter = 'ALL_WITH_TOKEN' | 'SINGLE_USER'

const audienceOptions = [
  { label: 'All users with token', value: 'ALL_WITH_TOKEN' },
  { label: 'Single user', value: 'SINGLE_USER' },
]

function resolveUserToken(user: UserDataProps) {
  return user.device?.fcmToken?.trim() || user.tokenFcm?.trim() || null
}

export function FormPublishCampaign({ campaign }: FormPublishCampaignProps) {
  const dispatch = useDispatch()
  const { closeModal } = useModal()
  const { showToast } = useToast()

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [audienceType, setAudienceType] =
    useState<AudienceFilter>('ALL_WITH_TOKEN')
  const [users, setUsers] = useState<UserDataProps[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  const eligibleUsers = useMemo(
    () => users.filter((user) => Boolean(resolveUserToken(user))),
    [users],
  )

  const refreshCampaigns = async () => {
    const campaigns = await fetchCampaigns()
    dispatch(handleSetCampaigns({ campaigns }))
  }

  const handlePublish = async () => {
    if (audienceType === 'SINGLE_USER' && !selectedUserId) {
      showToast('Select one user to publish the campaign', {
        type: 'warning',
        theme: 'light',
      })
      return
    }

    try {
      setIsLoading(true)
      await publishCampaign(
        campaign.id,
        audienceType,
        selectedUserId ?? undefined,
      )

      const metrics = await fetchCampaignMetrics(campaign.id)

      if (metrics.pushFailureCount > 0) {
        showToast(
          `Campaign published: ${metrics.pushSuccessCount} success, ${metrics.pushFailureCount} failed (${metrics.pushAttemptedCount} attempted)`,
          {
            type: 'warning',
            theme: 'light',
          },
        )
      } else {
        showToast(
          `Campaign published with full push acceptance (${metrics.pushSuccessCount}/${metrics.pushAttemptedCount})`,
          {
            type: 'success',
            theme: 'light',
          },
        )
      }

      await refreshCampaigns()
      closeModal()
    } catch (err: unknown) {
      const apiMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof err.response === 'object' &&
        err.response !== null &&
        'data' in err.response &&
        typeof err.response.data === 'object' &&
        err.response.data !== null &&
        'message' in err.response.data &&
        typeof err.response.data.message === 'string'
          ? err.response.data.message
          : 'Error publishing campaign'

      showToast(apiMessage, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true

    async function loadUsers() {
      try {
        setIsLoadingUsers(true)
        const response = await api.get('/users')

        if (!isMounted) return

        setUsers(response.data.data as UserDataProps[])
      } catch {
        if (!isMounted) return

        showToast('Error loading users for publishing', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        if (isMounted) {
          setIsLoadingUsers(false)
        }
      }
    }

    loadUsers()

    return () => {
      isMounted = false
    }
  }, [showToast])

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-bold text-gray-700">Publish campaign</h3>
        <p className="mt-1 text-sm text-gray-500">{campaign.title}</p>
      </div>

      <Select
        label="Audience"
        options={audienceOptions}
        value={audienceType}
        onChange={(event) =>
          setAudienceType(event.currentTarget.value as AudienceFilter)
        }
      />

      {audienceType === 'SINGLE_USER' && (
        <div>
          <h4 className="mb-3 text-sm font-bold">Select one user</h4>
          <div className="max-h-56 space-y-2 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-3">
            {isLoadingUsers && (
              <p className="text-sm text-gray-500">Loading users...</p>
            )}

            {!isLoadingUsers && eligibleUsers.length === 0 && (
              <p className="text-sm text-gray-500">
                No users with FCM token available.
              </p>
            )}

            {eligibleUsers.map((user) => {
              const label =
                user.name?.trim() || user.displayName?.trim() || 'No name'
              const email = user.email?.trim() || 'No email'

              return (
                <label
                  key={user.id}
                  className="flex cursor-pointer items-start gap-2 rounded border border-gray-200 bg-white px-3 py-2"
                >
                  <input
                    type="radio"
                    name="campaign-audience-user"
                    checked={selectedUserId === user.id}
                    onChange={() => setSelectedUserId(user.id)}
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-gray-700">
                      {label}
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                      {email}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>
        </div>
      )}

      <Button
        title="Publish campaign"
        variant="purple"
        isLoading={isLoading}
        onClick={handlePublish}
      />
    </div>
  )
}
